import { AllExceptionList } from "~/pages/findings/Exceptions/components/AllExceptionList";
import { Paginate } from "~/components/pagination/paginate";
import { ExceptionGroupRowProps } from "~/pages/findings/Exceptions/components/ExceptionsGroupRow";
import {
  ExceptionReviewAction,
  ExceptionType,
  ListExceptionGroupsOrder,
  ListExceptionGroupsOrderField,
  OrderDirection,
  useListSpaceExceptionsQuery,
} from "~/operations";

import { usePaginate } from "~/components/pagination/usePaginate";
import { FINDINGS_TABLE_INITIAL_PAGE_RANGE } from "~/components/pagination";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { Box } from "@mui/material";
import { Loading } from "~/components/loading";
import { ScopeType, useScope } from "~/hooks/useScope";
import { ExceptionTarget } from "./types";
import { ExceptionsFilterVariables } from "~/pages/shared/policy/hooks/usePolicyExceptionsFilterContext";

interface Props {
  handleReviewActionChange: ExceptionGroupRowProps["onReviewActionChange"];
  exceptionId: string | null;
  isReviewLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  loadingExceptionId: string | undefined;
  findingMrn?: string;
  exceptionTypes: ExceptionType[];
  assetMrn?: string;
  target?: ExceptionTarget;
  filterVariables?: ExceptionsFilterVariables;
}

export function ExceptionListWithPagination({
  handleReviewActionChange,
  exceptionId,
  isReviewLoading,
  loadingAction,
  loadingExceptionId,
  findingMrn,
  exceptionTypes,
  assetMrn,
  target,
  filterVariables,
}: Props) {
  const paginate = usePaginate({
    defaultPageSize: FINDINGS_TABLE_INITIAL_PAGE_RANGE.to,
  });
  const baseUrl = paginate.resetCursorUrl(location.href);
  const { activeScope } = useScope();
  const isPlatformScope = activeScope?.type === ScopeType.Region;

  const sort = useSort<ListExceptionGroupsOrder>({
    baseUrl,
    defaultSort: {
      field: ListExceptionGroupsOrderField.CreatedAt,
      direction: OrderDirection.Desc,
    },
    validFields: [
      ListExceptionGroupsOrderField.CreatedAt,
      ListExceptionGroupsOrderField.ValidUntil,
    ],
  });

  const { data: allExceptions, loading } = useListSpaceExceptionsQuery({
    variables: {
      ...paginate?.pageVars,
      input: {
        mrn: findingMrn,
        scopeMrn: assetMrn || activeScope?.mrn || "",
        types: exceptionTypes,
        orderBy: sort?.orderBy,
        filter: {
          includeDeleted: true,
          ...filterVariables?.filter,
        },
        includeChildScopes: isPlatformScope,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  if (loading) {
    return (
      <Box mt={3}>
        <Loading what="Exceptions" />
      </Box>
    );
  }

  const paginationProps = paginate.pageProps(
    allExceptions?.listExceptionGroups.pageInfo,
  );

  const exceptionGroups =
    allExceptions?.listExceptionGroups?.edges?.map((e) => e.node) || [];

  return (
    <>
      <AllExceptionList
        {...{
          allExceptionGroups: exceptionGroups,
          handleReviewActionChange,
          exceptionId,
          orderBy: sort.orderBy,
          sortColumnUrl: (field) =>
            paginate.resetCursorUrl(sort.sortColumnUrl(field)),
          isReviewLoading,
          loadingAction,
          loadingExceptionId,
          target,
        }}
      />

      <Paginate {...paginationProps} />
    </>
  );
}
